package com.vamvisions.main.pages.portfolio

import com.vamvisions.main.theme.AppStyleSheet
import com.vamvisions.main.theme.NARROW_SCREEN
import com.vamvisions.main.theme.REDUCED_MOTION_QUERY
import com.vamvisions.main.theme.REVEAL_GATE_CLASS
import com.vamvisions.main.theme.Tokens
import com.vamvisions.main.theme.cssVar
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.media

/** Where a timeline dot's centre sits below the top of its row, level with the card title. */
private const val DOT_CENTRE = "2.11rem"

/** The space between timeline rows. */
private const val ROW_GAP = "1.33rem"

/** The ring around a dot, in the page background colour, so the dot stands off the line in both schemes. */
private const val DOT_RING = "color-mix(in srgb, var(${Tokens.COLOR_BG}) 55%, transparent)"

/** The Portfolio page: intro, timeline, learning cards and education, on glass cards over the background. */
object PortfolioStyles : StyleSheet() {

    /** The page content, as wide as the Figma frames' content. */
    val page by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "4rem")
        property("max-width", "53.33rem")
        property("margin-inline", "auto")
        media(NARROW_SCREEN) {
            self style {
                property("gap", "2.67rem")
            }
        }
        // The page's section titles are larger than elsewhere, with more room below.
        desc(self, className(AppStyleSheet.sectionTitle)) style {
            property("font-size", "1.89rem")
        }
        desc(self, className(AppStyleSheet.sectionHeading)) style {
            property("margin-bottom", "1.33rem")
        }
        desc(self, className(AppStyleSheet.section)) style {
            property("margin", "0")
        }
    }

    val intro by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", "0.67rem")
        property("text-align", "center")
    }

    val introTitle by style {
        property("margin", "0")
        property("font-size", "clamp(1.8rem, 6vw, 2.6rem)")
        property("line-height", "1.2")
    }

    val introText by style {
        property("max-width", "43.33rem")
        property("margin", "0")
        property("font-size", "1.2rem")
        media(NARROW_SCREEN) {
            self style {
                property("font-size", "1rem")
            }
        }
    }

    /** The status messages and the content share the page's vertical rhythm. */
    val content by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "4rem")
        media(NARROW_SCREEN) {
            self style {
                property("gap", "2.67rem")
            }
        }
    }

    val timeline by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", ROW_GAP)
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
    }

    /**
     * A dot on the rail, then the card. Every row but the last draws the line from its dot down to the next row's
     * dot, so the whole line ends at the last dot.
     */
    val timelineRow by style {
        property("position", "relative")
        property("display", "flex")
        property("gap", "1.11rem")
        property("align-items", "flex-start")
        self + ":not(:last-child)::before" style {
            property("content", "\"\"")
            property("position", "absolute")
            property("top", DOT_CENTRE)
            property("bottom", "calc(-1 * ($ROW_GAP + $DOT_CENTRE))")
            property("left", "calc(0.67rem - 1px)")
            property("width", "2px")
            property("background-color", cssVar(Tokens.COLOR_BRAND))
            property("opacity", "0.5")
            property("transform-origin", "top")
            property("transition", "transform 0.6s ease-out")
            // The row's reveal delay, so the line follows its card.
            property("transition-delay", "inherit")
        }
    }

    /** The rail column. Its dot is centred on the line. */
    val rail by style {
        property("position", "relative")
        property("z-index", "1")
        property("flex-shrink", "0")
        property("width", "1.33rem")
        property("height", "3.11rem")
    }

    val dot by style {
        property("position", "absolute")
        property("top", "calc($DOT_CENTRE - 0.44rem)")
        property("left", "calc(0.67rem - 0.44rem)")
        property("width", "0.89rem")
        property("height", "0.89rem")
        property("background-color", cssVar(Tokens.COLOR_BRAND))
        property("border-radius", "50%")
        property("box-shadow", "0 0 0 4px $DOT_RING")
    }

    /** The current entry's dot: larger, with a soft glow. */
    val dotCurrent by style {
        property("top", "calc($DOT_CENTRE - 0.56rem)")
        property("left", "calc(0.67rem - 0.56rem)")
        property("width", "1.11rem")
        property("height", "1.11rem")
        property("box-shadow", "0 0 0 4px $DOT_RING, 0 0 14px 6px color-mix(in srgb, var(${Tokens.COLOR_BRAND}) 45%, transparent)")
    }

    /** An entry card: glass, with its parts stacked. */
    val entry by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "0.56rem")
        property("flex", "1")
        property("padding", "1.33rem")
        property("scroll-margin-top", cssVar(Tokens.SPACE_3))
    }

    /** The current entry's brand outline. */
    val entryCurrent by style {
        property("border", "1.5px solid ${cssVar(Tokens.COLOR_BRAND)}")
    }

    /** Name and period side by side, the role below; on phones the period moves under the role. */
    val entryHead by style {
        property("display", "grid")
        property("grid-template-columns", "minmax(0, 1fr) auto")
        property("grid-template-areas", "\"name period\" \"role role\"")
        property("gap", "0.56rem 0.89rem")
        media(NARROW_SCREEN) {
            self style {
                property("grid-template-columns", "minmax(0, 1fr)")
                property("grid-template-areas", "\"name\" \"role\" \"period\"")
                property("row-gap", "0.11rem")
            }
        }
    }

    val entryName by style {
        property("grid-area", "name")
        property("margin", "0")
        property("font-size", "1.33rem")
        property("line-height", "1.25")
        property("overflow-wrap", "anywhere")
    }

    val entryNameLink by style {
        property("color", cssVar(Tokens.COLOR_ON_BG))
        property("text-decoration", "none")
        self + ":hover" style {
            property("color", cssVar(Tokens.COLOR_BRAND))
            property("text-decoration", "underline")
        }
    }

    val entryPeriod by style {
        property("grid-area", "period")
        property("margin", "0")
        property("font-size", "0.78rem")
        property("font-weight", "600")
        property("line-height", "1.7")
        property("white-space", "nowrap")
        property("color", cssVar(Tokens.COLOR_MUTED))
    }

    val entryRole by style {
        property("grid-area", "role")
        property("margin", "0")
        property("font-size", "0.83rem")
        property("font-weight", "600")
        property("line-height", "1.4")
        property("color", cssVar(Tokens.COLOR_BRAND))
    }

    val entryBody by style {
        property("margin", "0")
        property("font-size", "0.83rem")
        property("line-height", "1.6")
    }

    /** Filled bullets, though the list sits inside the timeline list. */
    val entryList by style {
        property("margin", "0")
        property("list-style", "disc")
        property("padding-left", "1.2em")
        property("font-size", "0.83rem")
        property("line-height", "1.6")
    }

    val entryLinks by style {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "0.5rem 1.33rem")
    }

    val entryLink by style {
        property("font-size", "0.83rem")
        property("font-weight", "600")
        property("text-decoration", "none")
        self + ":hover" style {
            property("text-decoration", "underline")
        }
    }

    /** The learning cards side by side, as tall as each other; stacked on phones. */
    val learning by style {
        property("display", "grid")
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        property("gap", "1.33rem")
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        media(NARROW_SCREEN) {
            self style {
                property("grid-template-columns", "minmax(0, 1fr)")
            }
        }
    }

    val learningItem by style {
        property("display", "flex")
        property("min-width", "0")
    }

    init {
        // The line starts undrawn until its row is revealed, like the reveal's own start state.
        "html.$REVEAL_GATE_CLASS .$timelineRow.${AppStyleSheet.reveal}:not(.${AppStyleSheet.revealed})::before" style {
            property("transform", "scaleY(0)")
        }
        media(NARROW_SCREEN) {
            ".$page .${AppStyleSheet.sectionTitle}" style {
                property("font-size", "1.56rem")
            }
        }
        media(REDUCED_MOTION_QUERY) {
            ".$timelineRow::before" style {
                property("transform", "none", important = true)
                property("transition", "none")
            }
        }
    }
}
